const DAY_MS = 24 * 60 * 60 * 1000;
export const LINNWORKS_API_MAX_LOOKBACK_MS = 35 * DAY_MS;
export const LINNWORKS_FORWARD_SYNC_INITIAL_LOOKBACK_MS = 28 * DAY_MS;
export const LINNWORKS_SYNC_OVERLAP_MS = 60 * 1000;
export const LINNWORKS_INSTALL_TRACKING_PURPOSE = "redo:linnworks-install";

export const LinnworksLocality = { EU: "EU", US: "US", AS: "AS" } as const;
export type LinnworksLocality =
  (typeof LinnworksLocality)[keyof typeof LinnworksLocality];

// Installs that predate returns have no `oms` block and keep syncing orders;
// only a returns-only install (explicit `oms.enabled: false`) opts out.
export function isLinnworksOmsEnabled(integration: {
  oms?: { enabled?: boolean | null } | null;
}): boolean {
  return integration.oms?.enabled !== false;
}

/** Which vendor-page row started an install; carried in the signed tracking state. */
export const LinnworksInstallProduct = {
  OMS: "oms",
  RETURNS: "returns",
} as const;
export type LinnworksInstallProduct =
  (typeof LinnworksInstallProduct)[keyof typeof LinnworksInstallProduct];

// Tracking states signed before returns existed carry no product; they came
// from the OMS row.
export function parseLinnworksInstallProduct(
  value: unknown,
): LinnworksInstallProduct {
  return value === LinnworksInstallProduct.RETURNS
    ? LinnworksInstallProduct.RETURNS
    : LinnworksInstallProduct.OMS;
}

/**
 * Linnworks and Shopify SKUs differ in case and stray whitespace, so booking an
 * RMA and matching its actioned lines must compare them the same way.
 */
export function normalizeLinnworksSku(sku: string): string {
  return sku.trim().toLowerCase();
}

interface LinnworksRmaActionState {
  actioned: boolean;
  statusHeader: string | null;
}

/** Linnworks can report an RMA or line as PROCESSED without setting Actioned. */
export function isLinnworksRmaActioned(
  state: LinnworksRmaActionState,
): boolean {
  return state.actioned || state.statusHeader === "PROCESSED";
}

/** Every line of a completed header counts as actioned. */
export function linnworksActionedRmaLines<Line extends LinnworksRmaActionState>(
  header: LinnworksRmaActionState & { lines: Line[] },
): Line[] {
  return isLinnworksRmaActioned(header)
    ? header.lines
    : header.lines.filter(isLinnworksRmaActioned);
}
